class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const triggers = document.querySelectorAll('[data-cosmeau-cart-trigger]');
    triggers.forEach((trigger) => {
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', 'CartDrawer');
    });

    if (this.dataset.headerCartBound === 'true') return;
    this.dataset.headerCartBound = 'true';
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cosmeau-cart-trigger]');
      if (!trigger) return;
      event.preventDefault();
      const menuToggle = document.querySelector('.cosmeau-v6-menu-toggle:checked');
      if (menuToggle) menuToggle.checked = false;
      this.open(trigger);
    });
    document.addEventListener('keydown', (event) => {
      const trigger = event.target.closest?.('[data-cosmeau-cart-trigger]');
      if (!trigger || !['SPACE', 'ENTER'].includes(event.code.toUpperCase())) return;
      event.preventDefault();
      this.open(trigger);
    });
  }

  captureViewState() {
    const items = this.querySelector('[data-cosmeau-cart-scroll-body]') || this.querySelector('#CosmeauCartItems');
    const active = document.activeElement?.closest?.('[data-line-key]');
    return {
      scrollTop: items?.scrollTop || 0,
      activeLineKey: active?.dataset.lineKey || '',
      activeName: document.activeElement?.getAttribute?.('name') || '',
      progressWidth: this.querySelector('[data-cosmeau-cart-progress]')?.style.width || '',
    };
  }

  restoreViewState(state) {
    if (!state) return;
    const restore = () => {
      const items = this.querySelector('[data-cosmeau-cart-scroll-body]') || this.querySelector('#CosmeauCartItems');
      if (items) items.scrollTop = state.scrollTop;
      if (state.progressWidth) {
        const progress = this.querySelector('[data-cosmeau-cart-progress]');
        if (progress && !progress.style.width) progress.style.width = state.progressWidth;
      }
      if (state.activeLineKey && state.activeName) {
        this.querySelector(`[data-line-key="${CSS.escape(state.activeLineKey)}"] [name="${CSS.escape(state.activeName)}"]`)?.focus({ preventScroll: true });
      }
    };
    restore();
    requestAnimationFrame(restore);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('active')) return;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.classList.add('animate');
    requestAnimationFrame(() => this.classList.add('active'));
    this.addEventListener(
      'transitionend',
      () => {
        const container = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (container && focusElement) trapFocus(container, focusElement);
      },
      { once: true },
    );
    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  setEmptyState(isEmpty) {
    this.classList.toggle('is-empty', isEmpty);
    this.querySelector('#CosmeauCartItems')?.classList.toggle('is-empty', isEmpty);
    this.querySelector('#CosmeauCartFooter')?.classList.toggle('is-empty', isEmpty);
  }

  patchDynamicRegions(sectionHtml) {
    if (!sectionHtml) return;
    const sourceDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sourceDrawer = sourceDocument.querySelector('cart-drawer[data-cosmeau-cart]');
    const state = this.captureViewState();

    ['CosmeauCartEmpty', 'CosmeauCartItems', 'CosmeauCartFooter'].forEach((id) => {
      const target = this.querySelector(`#${id}`);
      const source = sourceDocument.querySelector(`#${id}`);
      if (target && source) target.innerHTML = source.innerHTML;
    });

    if (sourceDrawer) this.setEmptyState(sourceDrawer.classList.contains('is-empty'));
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.restoreViewState(state);
  }

  patchCartIcon(sectionHtml) {
    if (!sectionHtml) return;
    const source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('#cart-icon-bubble');
    const target = document.querySelector('#cart-icon-bubble');
    if (source && target) target.innerHTML = source.innerHTML;
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.patchDynamicRegions(parsedState.sections?.['cart-drawer']);
    this.patchCartIcon(parsedState.sections?.['cart-icon-bubble']);
    this.setHeaderCartIconAccessibility();
    window.CosmeauCartDrawer?.sync(
      typeof parsedState.item_count === 'number' ? parsedState : undefined,
    );
    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return source ? source.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CosmeauCartEmpty', section: 'cart-drawer', selector: '#CosmeauCartEmpty' },
      { id: 'CosmeauCartItems', section: 'cart-drawer', selector: '#CosmeauCartItems' },
      { id: 'CosmeauCartFooter', section: 'cart-drawer', selector: '#CosmeauCartFooter' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  async updateQuantity(line, quantity, event, name, variantId) {
    const lineNumber = Number.parseInt(line, 10);
    const requestedQuantity = Number.parseInt(quantity, 10);
    const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer[data-cosmeau-cart]');
    const input = this.querySelector(`#Drawer-quantity-${lineNumber}`);
    const previousQuantity = input?.getAttribute('value') || input?.value || '1';
    const viewState = drawer?.captureViewState?.();

    const showError = (message) => {
      const safeMessage = String(message || window.cartStrings?.error || 'Probeer het opnieuw.');
      if (input) input.value = previousQuantity;
      const lineError = this.querySelector(`#CartDrawer-LineItemError-${lineNumber} .cart-item__error-text`);
      if (lineError) lineError.textContent = safeMessage;
      window.CosmeauCartNotice?.show?.(safeMessage);
    };

    if (!Number.isInteger(lineNumber) || lineNumber < 1 || !Number.isInteger(requestedQuantity) || requestedQuantity < 0) {
      showError('De productregel kon niet worden gevonden. Vernieuw de winkelmand en probeer het opnieuw.');
      return;
    }

    try {
      this.enableLoading(lineNumber);
    } catch (error) {
      console.warn('[cosmeau-cart] loading state kon niet worden gestart', error);
    }

    try {
      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          line: lineNumber,
          quantity: requestedQuantity,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });

      let cart;
      try {
        cart = await response.json();
      } catch (_) {
        throw Object.assign(new Error(window.cartStrings?.error || 'De winkelmandservice gaf geen geldig antwoord.'), {
          code: 'SERVICE_UNAVAILABLE',
        });
      }

      if (!response.ok || cart.errors) {
        const apiMessage =
          typeof cart.errors === 'string'
            ? cart.errors
            : cart.description || window.cartStrings?.error || 'De winkelmand kon niet worden bijgewerkt.';
        throw Object.assign(new Error(apiMessage), { code: 'INVALID' });
      }
      if (!Number.isFinite(Number(cart.item_count)) || !Array.isArray(cart.items)) {
        throw Object.assign(new Error(window.cartStrings?.error || 'De winkelmandservice gaf een onvolledig antwoord.'), {
          code: 'SERVICE_UNAVAILABLE',
        });
      }

      window.CosmeauCartNotice?.hide?.();
      try {
        cart = await window.CosmeauCartDrawer?.reconcileRewards?.(cart) || cart;
      } catch (rewardError) {
        console.error('[cosmeau-cart] cadeaucontrole na aantalwijziging mislukte', rewardError);
        window.CosmeauCartNotice?.show?.('Je aantal is aangepast. Cadeaus worden opnieuw gecontroleerd.');
      }

      const lineError = this.querySelector(`#CartDrawer-LineItemError-${lineNumber} .cart-item__error-text`);
      if (lineError) lineError.textContent = '';

      try {
        drawer?.patchDynamicRegions(cart.sections?.['cart-drawer']);
        drawer?.patchCartIcon(cart.sections?.['cart-icon-bubble']);
        drawer?.setEmptyState?.(Number(cart.item_count) === 0);
        drawer?.restoreViewState?.(viewState);
      } catch (renderError) {
        console.error('[cosmeau-cart] cart is bijgewerkt, maar drawer patchen mislukte', renderError);
        window.CosmeauCartDrawer?.refreshSections?.(false).catch?.(() => {});
      }

      Promise.resolve(window.CosmeauCartDrawer?.sync?.(cart)).catch(() => {});
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart, variantId });
        }
      } catch (eventError) {
        console.warn('[cosmeau-cart] cart pub/sub mislukte', eventError);
      }
      document.dispatchEvent(new CustomEvent('cart_changed', { detail: { source: 'cart-items', cart } }));
    } catch (error) {
      console.error('[cosmeau-cart] line mutation failed', error);
      const message = error?.code === 'INVALID' ? error.message : window.cartStrings?.error;
      showError(message);
      try {
        this.dispatchCartErrorEvent(message, error?.code || 'SERVICE_UNAVAILABLE');
      } catch (_) {}
    } finally {
      try {
        this.disableLoading(lineNumber);
      } catch (_) {}
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
